import React from 'react';
import { FiCheckCircle as CheckCircle, FiTarget as Target, FiDownload as Download, FiMail as Mail } from 'react-icons/fi';

interface OnboardingData {
  name: string;
  company: string;
  email: string;
}

interface AssessmentOnboardingFormProps {
  isJoiningAssessment: boolean;
  onboardingSuccess: boolean;
  onboardingSuccessMessage: string;
  onboardingData: OnboardingData;
  onboardingError: string | null;
  onboardingLoading: boolean;
  handleOnboardingSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  handleOnboardingChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const AssessmentOnboardingForm: React.FC<AssessmentOnboardingFormProps> = ({
  isJoiningAssessment,
  onboardingSuccess,
  onboardingSuccessMessage,
  onboardingData,
  onboardingError,
  onboardingLoading,
  handleOnboardingSubmit,
  handleOnboardingChange,
}) => {
  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(to bottom, #0a0e1a 0%, #12192b 100%)' }}>
      <div className="flex flex-col items-center justify-center min-h-screen py-12 px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <img src="/aivilo-logo-horizontal.png" alt="Aivilo" className="h-16" />
        </div>

        <div className="text-center mb-8 max-w-2xl px-2">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
            AI Strategy Assessment
          </h1>
          <p className="text-base sm:text-lg text-gray-400">
            Discover the health of your organization's AI strategy in minutes
          </p>
        </div>

        <div className="w-full max-w-2xl">
          <div className="rounded-xl p-8 sm:p-10" style={{ background: 'rgba(31, 41, 55, 0.5)', backdropFilter: 'blur(10px)' }}>
            {onboardingSuccess ? (
              <div className="text-center py-8">
                <div className="mx-auto w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle className="w-10 h-10 text-blue-400" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Request Submitted!</h3>
                <p className="text-gray-300 text-lg leading-relaxed">
                  {onboardingSuccessMessage}
                </p>
                <div className="mt-8 pt-6 border-t border-gray-700">
                  <p className="text-sm text-gray-400">
                    You can close this window. We'll notify you via email once your access is approved.
                  </p>
                </div>
              </div>
            ) : (
              <form className="space-y-5" onSubmit={handleOnboardingSubmit}>
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-white mb-2">
                    Full Name
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    required
                    value={onboardingData.name}
                    onChange={handleOnboardingChange}
                    className="appearance-none block w-full px-4 py-3 rounded-lg bg-gray-700/50 border-0 text-gray-300 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="John Smith"
                  />
                </div>

                {!isJoiningAssessment && (
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-white mb-2">
                      Company
                    </label>
                    <input
                      id="company"
                      name="company"
                      type="text"
                      autoComplete="organization"
                      required
                      value={onboardingData.company}
                      onChange={handleOnboardingChange}
                      className="appearance-none block w-full px-4 py-3 rounded-lg bg-gray-700/50 border-0 text-gray-300 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Acme Corporation"
                    />
                  </div>
                )}

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                    Email address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-500" />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={onboardingData.email}
                      onChange={handleOnboardingChange}
                      className="appearance-none block w-full pl-10 pr-4 py-3 rounded-lg bg-gray-700/50 border-0 text-gray-300 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="your.email@company.com"
                    />
                  </div>
                  <p className="mt-2 text-sm text-gray-400">
                    We'll use this to save your progress and send you the results
                  </p>
                </div>

                {onboardingError && (
                  <div className="rounded-lg bg-red-900/30 border border-red-700 p-4">
                    <p className="text-sm text-red-200">{onboardingError}</p>
                  </div>
                )}

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={onboardingLoading}
                    className="w-full flex justify-center py-3.5 px-4 rounded-lg text-base font-semibold text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {onboardingLoading ? 'Starting...' : 'Start Assessment'}
                  </button>
                </div>
              </form>
            )}

            {!onboardingSuccess && (
              <div className="mt-8 sm:mt-10 pt-6 sm:pt-8 border-t border-gray-700">
                <h2 className="text-xl sm:text-2xl font-bold text-white text-center mb-4 sm:mb-6">
                  Instant Assessment Output
                </h2>

                <div className="space-y-4">
                  <div className="rounded-lg p-5" style={{ background: 'rgba(59, 130, 246, 0.1)' }}>
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                          <Target className="w-6 h-6 text-blue-400" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-white mb-1">
                          Personalized AI Strategy Score
                        </h3>
                        <p className="text-sm text-gray-400">
                          Comprehensive evaluation across 6 key dimensions
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg p-5" style={{ background: 'rgba(34, 197, 94, 0.1)' }}>
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                          <CheckCircle className="w-6 h-6 text-green-400" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-white mb-1">
                          Actionable Recommendations
                        </h3>
                        <p className="text-sm text-gray-400">
                          Priority-based roadmap for AI implementation
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg p-5" style={{ background: 'rgba(168, 85, 247, 0.1)' }}>
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                          <Download className="w-6 h-6 text-purple-400" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-white mb-1">
                          AWS Service Recommendations
                        </h3>
                        <p className="text-sm text-gray-400">
                          Tailored AWS solutions for your needs
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentOnboardingForm;
